import React, { useState, useEffect } from "react";
import {
  Container,
  Form,
  Button,
  Table,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Modal,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList,
} from "recharts";
import {
  FaUserPlus,
  FaUsers,
  FaChartPie,
  FaGlobeAmericas,
  FaEraser,
  FaFileExcel,
  FaExclamationTriangle,
} from "react-icons/fa";

function DataFormPage() {
  // Estados para el formulario
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    pais: "",
  });

  // Estado para manejar las categorías seleccionadas y sus transponders { "Categoría": "N° TX" }
  const [inscripciones, setInscripciones] = useState({});

  // Estados para la lista y UI
  const [pilotos, setPilotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });

  const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" && window.location.port === "3000" ? "http://localhost:5000/api" : "/api");

  const categoriasDisponibles = [
    "Buggy Nitro",
    "Buggy Eco",
    "Buggy +45",
    "Truggy Nitro",
    "Truggy Eco",
  ];

  const paisesConBandera = {
    Chile: "cl",
    Argentina: "ar",
    Brasil: "br",
    Bolivia: "bo",
    Ecuador: "ec",
    Colombia: "co",
    Paraguay: "py",
    Uruguay: "uy",
    Venezuela: "ve",
    Peru: "pe",
    USA: "us",
  };

  const getFlagUrl = (pais) => {
    const code = paisesConBandera[pais];
    return code ? `https://flagcdn.com/w40/${code}.png` : null;
  };

  // 1. Cargar datos al iniciar
  useEffect(() => {
    fetchPilotos();
  }, []);

  const fetchPilotos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/datos`);
      const data = await response.json();
      setPilotos(data);
      setLoading(false);
    } catch (error) {
      console.error("Error al cargar pilotos:", error);
      setLoading(false);
    }
  };

  // 2. Manejar cambios en el formulario
  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "nombre") {
      value = value
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleCheckChange = (categoria, isChecked) => {
    const updated = { ...inscripciones };
    if (isChecked) {
      updated[categoria] = ""; // Inicializa el transponder vacío si se marca
    } else {
      delete updated[categoria]; // Elimina si se desmarca
    }
    setInscripciones(updated);
  };

  const handleTransponderChange = (categoria, value) => {
    setInscripciones({ ...inscripciones, [categoria]: value });
  };

  // Estados para controlar los modales
  const [showClearModal, setShowClearModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPassword, setExportPassword] = useState("");
  const [showConfirmModifyModal, setShowConfirmModifyModal] = useState(false);
  const [pilotoExistente, setPilotoExistente] = useState(null);
  const [codigoModificacion, setCodigoModificacion] = useState("");
  const [codigoEnviado, setCodigoEnviado] = useState(false);
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);
  const [codigoFeedback, setCodigoFeedback] = useState({ texto: "", tipo: "" });

  const handleVerifyAndExport = async (e) => {
    e.preventDefault();

    try {
      // 1. Verificar contraseña de exportación
      const verifyRes = await fetch(
        `${API_BASE_URL}/exportar/verify-excel-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ password: exportPassword }),
        },
      );

      if (!verifyRes.ok) {
        alert("Contraseña de exportación incorrecta.");
        return;
      }

      // 2. Si es correcta, descargar el archivo enviando la contraseña autorizada
      const exportRes = await fetch(`${API_BASE_URL}/exportar/excel`, {
        headers: {
          "x-excel-password": exportPassword,
        },
      });

      if (!exportRes.ok) {
        alert("No se pudo descargar el archivo. Verifique su contraseña.");
        return;
      }

      const blob = await exportRes.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Pilotos_Sudam2027_${new Date().toLocaleDateString()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setShowExportModal(false);
      setExportPassword("");
    } catch (error) {
      alert("Error al conectar con el servidor para la exportación.");
    }
  };

  const handleClear = () => setShowClearModal(true);

  const confirmClear = () => {
    setFormData({ nombre: "", email: "", pais: "" });
    setInscripciones({});
    setMensaje({ texto: "", tipo: "" });
    setShowClearModal(false);
  };

  // 3. Ejecutar guardado (creación o modificación)
  const executeSave = async (modificar = false, codigoVerif = "") => {
    setIsSaving(true);
    setMensaje({ texto: "", tipo: "" });
    setCodigoFeedback({ texto: "", tipo: "" });

    try {
      const response = await fetch(`${API_BASE_URL}/datos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          inscripciones,
          modificar,
          codigoVerificacion: codigoVerif,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Si el backend reporta conflicto de correo existente
        if (response.status === 409 && result.exists) {
          setIsSaving(false);
          setPilotoExistente(result.piloto);
          setShowConfirmModifyModal(true);
          return;
        }

        // Si requiere código de verificación o es incorrecto
        if (result.requiresCode) {
          setIsSaving(false);
          setCodigoFeedback({ texto: result.error, tipo: "danger" });
          return;
        }

        throw new Error(result.error || "Error al procesar la inscripción");
      }

      setShowConfirmModifyModal(false);
      setCodigoModificacion("");
      setCodigoEnviado(false);
      setCodigoFeedback({ texto: "", tipo: "" });
      setMensaje({
        texto: modificar
          ? "¡Datos del piloto modificados con éxito!"
          : "¡Inscripciones guardadas con éxito!",
        tipo: "success",
      });
      setFormData({ nombre: "", email: "", pais: "" });
      setInscripciones({});
      fetchPilotos(); // Recargar la tabla
      setIsSaving(false);
      setShowResultModal(true);
    } catch (error) {
      setIsSaving(false);
      setMensaje({
        texto: error.message || "Error de conexión con el servidor",
        tipo: "danger",
      });
      setShowResultModal(true);
    }
  };

  // Guardar registro: primero verifica si el correo ya existe
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje({ texto: "", tipo: "" });

    const categoriasSeleccionadas = Object.keys(inscripciones);

    if (categoriasSeleccionadas.length === 0) {
      setMensaje({
        texto: "Debes seleccionar al menos una categoría",
        tipo: "warning",
      });
      return;
    }

    // Validar que todos los transponders seleccionados tengan valor
    if (categoriasSeleccionadas.some((cat) => !inscripciones[cat] || !inscripciones[cat].trim())) {
      setMensaje({
        texto:
          "Por favor, ingresa el transponder para todas las categorías seleccionadas",
        tipo: "warning",
      });
      return;
    }

    const emailTrim = (formData.email || "").trim();
    if (!emailTrim) {
      setMensaje({
        texto: "Por favor, ingresa un correo electrónico válido",
        tipo: "warning",
      });
      return;
    }

    setIsSaving(true);

    try {
      // 1. Buscar en la base de datos si el correo ya existe
      const verifyRes = await fetch(
        `${API_BASE_URL}/datos/verificar-email?email=${encodeURIComponent(emailTrim)}`
      );
      const verifyData = await verifyRes.json();

      if (verifyData.exists) {
        setIsSaving(false);
        setPilotoExistente(verifyData.piloto);
        setShowConfirmModifyModal(true);
        return;
      }

      // Si no existe, guardar directamente como nuevo piloto
      await executeSave(false);
    } catch (error) {
      console.error("Error al verificar correo:", error);
      // Si la verificación previa falla por red, intentar guardar (el backend también valida)
      await executeSave(false);
    }
  };

  // Solicitar código de 6 dígitos al correo
  const handleSolicitarCodigo = async () => {
    setEnviandoCodigo(true);
    setCodigoFeedback({ texto: "", tipo: "" });

    try {
      const res = await fetch(`${API_BASE_URL}/datos/solicitar-codigo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error al enviar código");
      setCodigoEnviado(true);
      setCodigoFeedback({
        texto: "Código enviado a tu correo. Revisa tu bandeja de entrada o spam.",
        tipo: "success",
      });
    } catch (err) {
      setCodigoFeedback({ texto: err.message, tipo: "danger" });
    } finally {
      setEnviandoCodigo(false);
    }
  };

  // Si el usuario indica que NO desea modificar los datos o cancela
  const handleCancelModify = () => {
    setShowConfirmModifyModal(false);
    setCodigoModificacion("");
    setCodigoEnviado(false);
    setCodigoFeedback({ texto: "", tipo: "" });
    setFormData({ nombre: "", email: "", pais: "" });
    setInscripciones({});
    setPilotoExistente(null);
    setMensaje({ texto: "", tipo: "" });
  };

  // 4. Lógica de Estadísticas
  // Calculamos las estadísticas basándonos siempre en la lista oficial de categorías
  const statsCategorias = categoriasDisponibles.reduce((acc, cat) => {
    acc[cat] = pilotos.filter((p) => {
      const pCat = p.categoria || "";
      if (cat === "Buggy Nitro")
        return pCat === "Buggy Nitro" || pCat === "1/8 Buggy" || pCat === "1/8 Nitro Buggy";
      if (cat === "Buggy Eco")
        return pCat === "Buggy Eco" || pCat === "1/8 Electrico" || pCat === "1/8 E-Buggy";
      if (cat === "Buggy +45")
        return pCat === "Buggy +45";
      if (cat === "Truggy Nitro")
        return pCat === "Truggy Nitro" || pCat === "1/8 Truggy" || pCat === "1/8 Nitro Truggy";
      if (cat === "Truggy Eco")
        return pCat === "Truggy Eco";
      return pCat === cat;
    }).length;
    return acc;
  }, {});

  const statsPaises = pilotos.reduce((acc, p) => {
    acc[p.pais] = (acc[p.pais] || 0) + 1;
    return acc;
  }, {});

  const COLORS = [
    "#0d6efd", // Azul (Buggy Nitro)
    "#198754", // Verde (Buggy Eco)
    "#fd7e14", // Naranja (Buggy +45)
    "#dc3545", // Rojo (Truggy Nitro)
    "#6f42c1", // Púrpura (Truggy Eco)
    "#0dcaf0", // Cyan
  ];

  // Función para obtener siempre el mismo color por categoría
  const getCategoryColor = (catName) => {
    if (catName === "Buggy Nitro" || catName === "1/8 Buggy" || catName === "1/8 Nitro Buggy")
      return "#0d6efd";
    if (catName === "Buggy Eco" || catName === "1/8 Electrico" || catName === "1/8 E-Buggy")
      return "#198754";
    if (catName === "Buggy +45")
      return "#fd7e14";
    if (catName === "Truggy Nitro" || catName === "1/8 Truggy" || catName === "1/8 Nitro Truggy")
      return "#dc3545";
    if (catName === "Truggy Eco")
      return "#6f42c1";

    const index = categoriasDisponibles.indexOf(catName);
    return COLORS[index !== -1 ? index % COLORS.length : 0];
  };

  // Preparar datos para los gráficos
  const dataCategorias = categoriasDisponibles.map((cat) => ({
    name: cat,
    value: statsCategorias[cat] || 0,
  }));

  const dataPaises = Object.entries(statsPaises).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <Container className="py-4 mb-5">
      <h2 className="text-center mb-4">Inscripción Sudam 2027</h2>

      {/* Formulario y Estadísticas */}
      <Row className="mb-5">
        <Col lg={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-primary text-white py-3">
              <FaUserPlus className="me-2" /> Datos del Piloto
            </Card.Header>
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Completo</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    placeholder="Ej: Juan Pérez"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Correo Electrónico (Email)</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Ej: piloto@ejemplo.com"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>País</Form.Label>
                  <InputGroup>
                    <Form.Select
                      name="pais"
                      value={formData.pais}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Selecciona un país</option>
                      <option value="Chile">Chile</option>
                      <option value="Argentina">Argentina</option>
                      <option value="Brasil">Brasil</option>
                      <option value="Bolivia">Bolivia</option>
                      <option value="Ecuador">Ecuador</option>
                      <option value="Colombia">Colombia</option>
                      <option value="Paraguay">Paraguay</option>
                      <option value="Uruguay">Uruguay</option>
                      <option value="Venezuela">Venezuela</option>
                      <option value="Peru">Peru</option>
                      <option value="USA">USA</option>
                      <option value="Otro">Otro</option>
                    </Form.Select>
                    {formData.pais && (
                      <InputGroup.Text className="bg-light px-3 d-flex align-items-center justify-content-center">
                        {getFlagUrl(formData.pais) ? (
                          <img
                            src={getFlagUrl(formData.pais)}
                            alt={`Bandera de ${formData.pais}`}
                            style={{
                              width: "28px",
                              height: "20px",
                              objectFit: "cover",
                              borderRadius: "3px",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: "1.2rem" }}>🌐</span>
                        )}
                      </InputGroup.Text>
                    )}
                  </InputGroup>
                </Form.Group>

                <Form.Label className="fw-bold mb-2">
                  Categorías e Inscripción
                </Form.Label>
                {categoriasDisponibles.map((cat) => (
                  <Card
                    key={cat}
                    className={`mb-2 border-0 shadow-sm transition-all ${
                      inscripciones[cat] !== undefined ? "text-white" : ""
                    }`}
                    style={{
                      borderLeft: `6px solid ${getCategoryColor(cat)}`,
                      backgroundColor:
                        inscripciones[cat] !== undefined
                          ? getCategoryColor(cat)
                          : "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    <Card.Body className="py-2 px-3">
                      <Form.Check
                        type="checkbox"
                        id={`cat-${cat}`}
                        label={cat}
                        checked={Object.prototype.hasOwnProperty.call(
                          inscripciones,
                          cat,
                        )}
                        onChange={(e) =>
                          handleCheckChange(cat, e.target.checked)
                        }
                      />
                      {inscripciones[cat] !== undefined && (
                        <Form.Control
                          size="sm"
                          className="mt-2"
                          placeholder="N° Transponder para esta categoría"
                          value={inscripciones[cat]}
                          onChange={(e) =>
                            handleTransponderChange(cat, e.target.value)
                          }
                          required
                        />
                      )}
                    </Card.Body>
                  </Card>
                ))}

                <div className="d-flex gap-2 mt-3">
                  <Button
                    variant="primary"
                    type="submit"
                    className="flex-grow-1 py-2 fw-bold shadow-sm"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Guardando...
                      </>
                    ) : (
                      "GUARDAR REGISTRO"
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleClear}
                    title="Limpiar Formulario"
                  >
                    <FaEraser />
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mt-4 mt-lg-0">
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-dark text-white py-3">
              <FaChartPie className="me-2" /> Estadísticas
            </Card.Header>
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <h1 className="display-4 fw-bold text-primary">
                  {pilotos.length}
                </h1>
                <p className="text-muted text-uppercase">Pilotos Inscritos</p>
              </div>
              <h6 className="fw-bold">
                <FaUsers className="me-2" /> Por Categoría:
              </h6>
              <div className="mb-3">
                {categoriasDisponibles.map((cat) => (
                  <Badge
                    key={cat}
                    className="me-2 mb-2 p-2 border-0 text-white shadow"
                    style={{
                      backgroundColor: getCategoryColor(cat),
                      fontSize: "0.9em",
                    }}
                  >
                    {cat}: {statsCategorias[cat] || 0}
                  </Badge>
                ))}
              </div>

              {/* Gráfico de Barras para Categorías */}
              <div style={{ width: "100%", height: 200 }} className="mb-4">
                <ResponsiveContainer>
                  <BarChart data={dataCategorias}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" fontSize={10} interval={0} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="value" position="top" fontSize={12} />
                      {dataCategorias.map((entry) => (
                        <Cell
                          key={`cell-${entry.name}`}
                          fill={getCategoryColor(entry.name)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <h6 className="fw-bold">
                <FaGlobeAmericas className="me-2" /> Por País:
              </h6>
              <div>
                {Object.entries(statsPaises).map(([pais, cant], index) => (
                  <Badge
                    key={pais}
                    className="me-2 mb-2 p-2 border-0 text-white"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  >
                    {pais}: {cant}
                  </Badge>
                ))}
              </div>

              {/* Gráfico de Dona para Países */}
              <div style={{ width: "100%", height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={dataPaises}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label
                    >
                      {dataPaises.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla de Pilotos */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Pilotos Confirmados</h3>
        <Button
          variant="success"
          onClick={() => setShowExportModal(true)}
          className="shadow-sm"
        >
          <FaFileExcel className="me-2" /> Exportar a Excel
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Table striped bordered hover responsive className="mb-0">
          <thead className="table-dark text-center">
            <tr>
              <th>Nombre</th>
              <th>País</th>
              <th style={{ width: "25%" }}>Categoría</th>
              <th style={{ width: "20%" }}>N° Transponder</th>
            </tr>
          </thead>
          <tbody className="align-middle text-center">
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center">
                  Cargando datos...
                </td>
              </tr>
            ) : pilotos.length > 0 ? (
              pilotos.map((p) => (
                <tr key={p.id}>
                  <td className="fw-bold text-start ps-3">{p.nombre}</td>
                  <td>
                    {getFlagUrl(p.pais) && (
                      <img
                        src={getFlagUrl(p.pais)}
                        alt={`Bandera de ${p.pais}`}
                        style={{
                          width: "22px",
                          height: "15px",
                          objectFit: "cover",
                          marginRight: "6px",
                          verticalAlign: "middle",
                          borderRadius: "2px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                        }}
                      />
                    )}
                    {p.pais}
                  </td>
                  <td>
                    <Badge
                      className="p-2 w-100 border-0 text-white"
                      style={{ backgroundColor: getCategoryColor(p.categoria) }}
                    >
                      {p.categoria}
                    </Badge>
                  </td>
                  <td>
                    <small className="text-muted fw-bold me-1">TX:</small>
                    <code
                      className="text-primary fw-bold"
                      style={{ fontSize: "1.1em" }}
                    >
                      {p.transponder}
                    </code>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  Aún no hay inscritos.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      {/* Modal para Password de Exportación */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Seguridad de Exportación</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleVerifyAndExport}>
          <Modal.Body>
            <p>
              Ingrese la contraseña autorizada para descargar la base de datos
              de pilotos.
            </p>
            <Form.Group>
              <Form.Control
                type="password"
                placeholder="Contraseña de Excel"
                value={exportPassword}
                onChange={(e) => setExportPassword(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowExportModal(false)}
            >
              Cancelar
            </Button>
            <Button variant="success" type="submit">
              Verificar y Descargar
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de Confirmación para Limpiar Formulario */}
      <Modal
        show={showClearModal}
        onHide={() => setShowClearModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Limpieza</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas limpiar el formulario? Se perderán todos
          los datos que no hayas guardado.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClearModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmClear}>
            Limpiar Todo
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmación si el Correo ya Existe */}
      <Modal
        show={showConfirmModifyModal}
        onHide={handleCancelModify}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title className="d-flex align-items-center">
            <FaExclamationTriangle className="me-2" /> Correo ya registrado
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <p className="mb-2">
            El correo electrónico <strong>{formData.email}</strong> ya se encuentra registrado en el sistema
            {pilotoExistente?.nombre && (
              <> a nombre del piloto <span className="fw-bold text-primary">{pilotoExistente.nombre}</span></>
            )}.
          </p>

          <Alert variant="info" className="small py-2 mb-3">
            Por seguridad, para modificar una inscripción debes ingresar un código de 6 dígitos que enviaremos a tu correo.
          </Alert>

          <div className="alert alert-light border border-warning p-3 mb-3">
            <h6 className="fw-bold text-dark mb-2">Nueva información a registrar:</h6>
            <ul className="mb-0 ps-3 small text-muted">
              <li><strong>Nombre:</strong> {formData.nombre}</li>
              <li><strong>País:</strong> {formData.pais}</li>
              <li>
                <strong>Categorías:</strong>{" "}
                {Object.entries(inscripciones)
                  .map(([cat, tx]) => `${cat} (TX: ${tx})`)
                  .join(", ")}
              </li>
            </ul>
          </div>

          {!codigoEnviado ? (
            <div className="text-center my-3">
              <Button
                variant="outline-primary"
                onClick={handleSolicitarCodigo}
                disabled={enviandoCodigo}
                className="w-100 py-2 fw-bold"
              >
                {enviandoCodigo ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Enviando código...
                  </>
                ) : (
                  "Enviar código de seguridad al correo"
                )}
              </Button>
            </div>
          ) : (
            <div className="my-3 p-3 bg-light rounded border">
              <Form.Group className="mb-2">
                <Form.Label className="fw-bold text-dark">Código de verificación (6 dígitos)</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={6}
                  placeholder="Ej: 123456"
                  value={codigoModificacion}
                  onChange={(e) => setCodigoModificacion(e.target.value.replace(/\D/g, ""))}
                  autoFocus
                  className="text-center fw-bold fs-5"
                />
              </Form.Group>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">Revisa tu bandeja de entrada o spam.</small>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={handleSolicitarCodigo}
                  disabled={enviandoCodigo}
                >
                  {enviandoCodigo ? "Enviando..." : "Reenviar código"}
                </Button>
              </div>
            </div>
          )}

          {codigoFeedback.texto && (
            <Alert variant={codigoFeedback.tipo} className="mt-2 py-2 small mb-0">
              {codigoFeedback.texto}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="secondary" onClick={handleCancelModify}>
            Cancelar
          </Button>
          {codigoEnviado && (
            <Button
              variant="warning"
              className="fw-bold"
              disabled={codigoModificacion.length < 6 || isSaving}
              onClick={() => executeSave(true, codigoModificacion)}
            >
              {isSaving ? "Modificando..." : "Confirmar Modificación"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal de Resultado de Inscripción (Guardar) */}
      <Modal
        show={showResultModal}
        onHide={() => setShowResultModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          className={
            mensaje.tipo === "success"
              ? "bg-success text-white"
              : "bg-danger text-white"
          }
        >
          <Modal.Title>
            {mensaje.tipo === "success" ? "¡Éxito!" : "Atención"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3">
            {mensaje.tipo === "success" ? (
              <div className="display-1 text-success">✓</div>
            ) : (
              <div className="display-1 text-danger">✕</div>
            )}
          </div>
          <h5>{mensaje.texto}</h5>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={mensaje.tipo === "success" ? "success" : "danger"}
            onClick={() => setShowResultModal(false)}
            className="w-100"
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
export default DataFormPage;
